/**
 * Het Koffiepaleis - Global JavaScript
 * Core functionality voor de webshop
 */

(function() {
  'use strict';

  const themeStrings = window.themeStrings || {};
  const i18n = {
    addToCart: themeStrings.addToCart || 'In winkelwagen',
    addToCartDelayed: themeStrings.addToCartDelayed || '',
    backorderPropertyName: themeStrings.backorderPropertyName || '',
    backorderPropertyValue: themeStrings.backorderPropertyValue || '',
    addingToCart: themeStrings.addingToCart || 'Adding...',
    addedToCart: themeStrings.addedToCart || 'Added!',
    cartError: themeStrings.cartError || 'Something went wrong. Please try again.',
    addToCartError: themeStrings.addToCartError || 'Failed — please try again',
    error: themeStrings.error || 'Error'
  };

  // ============================================
  // Cart API Functions
  // ============================================
  
  const CartAPI = {
    normalizeQuantity(value) {
      const parsed = parseInt(value, 10);
      return Number.isNaN(parsed) || parsed < 1 ? 1 : parsed;
    },

    hasProperties(properties = {}) {
      return properties && Object.keys(properties).length > 0;
    },

    hasLineProperties(item) {
      return item && item.properties && Object.keys(item.properties).length > 0;
    },

    getVariantQuantity(cart, variantId) {
      if (!cart || !Array.isArray(cart.items)) return 0;

      return cart.items.reduce((total, item) => {
        return item.variant_id === variantId ? total + item.quantity : total;
      }, 0);
    },

    getCartAddProduct(cart, variantId) {
      if (!cart || !Array.isArray(cart.items)) return null;

      return cart.items.find(item => item.variant_id === variantId) || null;
    },

    dispatchCartEvents(cart, variantId, beforeQuantity) {
      const currentQuantity = this.getVariantQuantity(cart, variantId);
      const addedQuantity = Math.max(0, currentQuantity - beforeQuantity);
      const item = this.getCartAddProduct(cart, variantId);

      if (addedQuantity > 0 && item) {
        document.dispatchEvent(new CustomEvent('cart:add', {
          detail: {
            product: {
              id: item.product_id,
              title: item.product_title,
              price: item.price,
              vendor: item.vendor,
              type: item.product_type,
              variant: item.variant_title
            },
            quantity: addedQuantity
          }
        }));
      }

      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
    },

    async changeLine(line, quantity) {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          line: line,
          quantity: quantity
        })
      });

      const payload = await response.json();
      if (!response.ok) {
        const error = new Error(payload.description || payload.message || 'Update cart failed');
        error.status = response.status;
        error.payload = payload;
        throw error;
      }

      return payload;
    },

    async changeLineWithClamp(line, itemKey, currentQuantity, targetQuantity) {
      try {
        return await this.changeLine(line, targetQuantity);
      } catch (error) {
        if (targetQuantity <= 0) throw error;

        const cart = await this.get();
        const item = cart.items[line - 1];
        if (!item || String(item.key) !== String(itemKey) || targetQuantity <= item.quantity) {
          throw error;
        }

        return this.stepLineQuantityUp(line, itemKey, item.quantity, targetQuantity);
      }
    },

    async stepLineQuantityUp(line, itemKey, currentQuantity, targetQuantity) {
      if (currentQuantity >= targetQuantity) {
        return this.get();
      }

      const nextQuantity = currentQuantity + 1;
      try {
        const cart = await this.changeLine(line, nextQuantity);
        const item = cart.items[line - 1];

        if (!item || String(item.key) !== String(itemKey) || item.quantity <= currentQuantity) {
          return cart;
        }

        return this.stepLineQuantityUp(line, itemKey, item.quantity, targetQuantity);
      } catch (error) {
        return this.get();
      }
    },

    /**
     * Voeg product toe aan cart
     */
    async add(variantId, quantity = 1, properties = {}) {
      const numericVariantId = parseInt(variantId, 10);
      const requestedQuantity = this.normalizeQuantity(quantity);
      const beforeCart = await this.get();
      const beforeQuantity = this.getVariantQuantity(beforeCart, numericVariantId);

      try {
        let cart;
        const existingLineIndex = this.hasProperties(properties)
          ? -1
          : beforeCart.items.findIndex(item => item.variant_id === numericVariantId && !this.hasLineProperties(item));

        if (existingLineIndex !== -1) {
          const existingItem = beforeCart.items[existingLineIndex];
          cart = await this.changeLineWithClamp(
            existingLineIndex + 1,
            existingItem.key,
            existingItem.quantity,
            existingItem.quantity + requestedQuantity
          );
        } else {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              id: variantId,
              quantity: requestedQuantity,
              properties: properties
            })
          });

          if (!response.ok) {
            const payload = await response.json().catch(() => ({}));
            const error = new Error(payload.description || payload.message || 'Add to cart failed');
            error.status = response.status;
            error.payload = payload;
            throw error;
          }

          cart = await this.get();
        }

        this.dispatchCartEvents(cart, numericVariantId, beforeQuantity);
        return cart;
      } catch (error) {
        console.error('Cart add error:', error);
        if (error.status === 422) {
          const cart = await this.get();
          this.dispatchCartEvents(cart, numericVariantId, beforeQuantity);
          return cart;
        }

        throw error;
      }
    },

    /**
     * Update cart item quantity
     */
    async update(line, quantity) {
      try {
        const cart = await this.changeLine(line, quantity);
        
        // Dispatch custom event
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
        
        return cart;
      } catch (error) {
        console.error('Cart update error:', error);
        throw error;
      }
    },

    /**
     * Get current cart state
     */
    async get() {
      try {
        const response = await fetch('/cart.js');
        if (!response.ok) {
          const error = new Error('Cart fetch failed');
          error.status = response.status;
          throw error;
        }
        const cart = await response.json();
        
        // Update cart count in header
        this.updateCartCount(cart.item_count);
        
        return cart;
      } catch (error) {
        console.error('Cart get error:', error);
        throw error;
      }
    },

    /**
     * Update cart count badge in header
     */
    updateCartCount(count) {
      const badges = document.querySelectorAll('[data-cart-count]');
      badges.forEach(badge => {
        badge.textContent = count;
        badge.style.display = count > 0 ? '' : 'none';
      });
    }
  };

  // ============================================
  // Add to Cart Form Handler
  // ============================================

  /**
   * Bouwt properties-object uit form fields met name properties[…] (FormData slaat uitgeschakelde velden over).
   */
  function cartLinePropertiesFromFormData(formData) {
    const props = {};
    if (!formData) return props;
    formData.forEach((value, key) => {
      const match = /^properties\[(.+)\]$/.exec(key);
      if (match && String(value).length > 0) {
        props[match[1]] = String(value);
      }
    });
    return props;
  }

  function initAddToCartForms() {
    document.querySelectorAll('form[action="/cart/add"]').forEach(form => {
      form.addEventListener('submit', async function(e) {
        e.preventDefault();
        
        const submitBtn = form.querySelector('[type="submit"]');
        const originalText = submitBtn.innerHTML;
        
        // Disable button
        submitBtn.disabled = true;
        submitBtn.innerHTML = '<span class="spinner"></span> ' + i18n.addingToCart;
        
        try {
          const formData = new FormData(form);
          const variantId = formData.get('id');
          const quantity = parseInt(formData.get('quantity')) || 1;
          const properties = cartLinePropertiesFromFormData(formData);

          await CartAPI.add(variantId, quantity, properties);
          
          // Success state
          submitBtn.innerHTML = '✓ ' + i18n.addedToCart;
          
          // Open cart drawer
          document.dispatchEvent(new Event('cart:open'));
          
          // Reset button after delay
          setTimeout(() => {
            submitBtn.innerHTML = originalText;
            submitBtn.disabled = false;
          }, 2000);
          
        } catch (error) {
          submitBtn.innerHTML = i18n.cartError;
          setTimeout(() => {
            submitBtn.innerHTML = originalText;
            submitBtn.disabled = false;
          }, 2000);
        }
      });
    });
  }

  // ============================================
  // Quick Add Buttons
  // ============================================
  
  function initQuickAddButtons() {
    document.addEventListener('click', async function(e) {
      const quickAddBtn = e.target.closest('[data-quick-add]');
      if (!quickAddBtn) return;
      
      e.preventDefault();
      e.stopPropagation(); // Voorkom dat de product link wordt gevolgd
      
      const variantId = quickAddBtn.dataset.quickAdd;
      const originalContent = quickAddBtn.innerHTML;
      
      quickAddBtn.disabled = true;
      quickAddBtn.innerHTML = '<span class="spinner" style="width:16px;height:16px;border-width:2px;"></span>';
      
      try {
        const props = getBackorderCartPropertiesFromElement(quickAddBtn);
        await CartAPI.add(variantId, 1, props);
        quickAddBtn.innerHTML = '✓';
        
        // Open cart drawer na succesvolle toevoeging
        document.dispatchEvent(new Event('cart:open'));
        
        setTimeout(() => {
          quickAddBtn.innerHTML = originalContent;
          quickAddBtn.disabled = false;
        }, 1500);
        
      } catch (error) {
        console.error('Quick add error:', error);
        quickAddBtn.innerHTML = '!';
        setTimeout(() => {
          quickAddBtn.innerHTML = originalContent;
          quickAddBtn.disabled = false;
        }, 1500);
      }
    });
  }

  function normalizeQuickQuantity(rawValue) {
    const parsed = parseInt(rawValue, 10);
    return Number.isNaN(parsed) || parsed < 0 ? 0 : parsed;
  }

  function getQuickOrderControls(trigger) {
    return trigger.closest('[data-quick-order-controls]');
  }

  function getQuickQuantityInput(trigger) {
    const controls = getQuickOrderControls(trigger);
    if (!controls) return null;
    return controls.querySelector('[data-quick-quantity]');
  }

  function getRelatedQuickQuantityInput(trigger, variantId) {
    const controls = getQuickOrderControls(trigger);
    if (controls) {
      return controls.querySelector('[data-quick-quantity]');
    }

    const scope = trigger.closest('article, .product-card, .equipment-card, [data-product-card]') || document;
    const selector = variantId
      ? `[data-quick-order-controls][data-variant-id="${variantId}"] [data-quick-quantity]`
      : '[data-quick-order-controls] [data-quick-quantity]';

    return scope.querySelector(selector);
  }

  function setQuickQuantity(input, nextValue) {
    if (!input) return;
    input.value = normalizeQuickQuantity(nextValue);
  }

  function initQuickQuantityControls() {
    document.addEventListener('click', async function(e) {
      const decreaseBtn = e.target.closest('[data-quick-qty-decrease]');
      if (decreaseBtn) {
        e.preventDefault();
        e.stopPropagation();

        const input = getQuickQuantityInput(decreaseBtn);
        const currentValue = normalizeQuickQuantity(input ? input.value : 0);
        setQuickQuantity(input, Math.max(0, currentValue - 1));
        return;
      }

      const increaseBtn = e.target.closest('[data-quick-qty-increase]');
      if (increaseBtn) {
        e.preventDefault();
        e.stopPropagation();

        const input = getQuickQuantityInput(increaseBtn);
        const currentValue = normalizeQuickQuantity(input ? input.value : 0);
        setQuickQuantity(input, currentValue + 1);
        return;
      }

      const addWithQuantityBtn = e.target.closest('[data-quick-add-with-quantity]');
      if (!addWithQuantityBtn) return;

      e.preventDefault();
      e.stopPropagation();

      const variantId = addWithQuantityBtn.dataset.quickAddWithQuantity;
      const quantityInput = getQuickQuantityInput(addWithQuantityBtn);
      const requestedQuantity = normalizeQuickQuantity(quantityInput ? quantityInput.value : 0);
      const quantity = requestedQuantity > 0 ? requestedQuantity : 1;
      const originalContent = addWithQuantityBtn.textContent;

      addWithQuantityBtn.disabled = true;
      addWithQuantityBtn.textContent = '...';

      try {
        await CartAPI.add(variantId, quantity);
        document.dispatchEvent(new Event('cart:open'));
        addWithQuantityBtn.textContent = '✓';
        setQuickQuantity(quantityInput, 1);

        setTimeout(() => {
          addWithQuantityBtn.textContent = originalContent;
          addWithQuantityBtn.disabled = false;
        }, 1500);
      } catch (error) {
        console.error('Quick add with quantity error:', error);
        const errorMsg = error.payload?.description || i18n.addToCartError;
        addWithQuantityBtn.textContent = errorMsg;
        addWithQuantityBtn.classList.add('button--error');
        setTimeout(() => {
          addWithQuantityBtn.textContent = originalContent;
          addWithQuantityBtn.classList.remove('button--error');
          addWithQuantityBtn.disabled = false;
        }, 2500);
      }
    });

    document.addEventListener('input', function(e) {
      const quantityInput = e.target.closest('[data-quick-quantity]');
      if (!quantityInput) return;

      quantityInput.value = normalizeQuickQuantity(quantityInput.value);
    });

    document.addEventListener('keydown', function(e) {
      const quantityInput = e.target.closest('[data-quick-quantity]');
      if (!quantityInput) return;

      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setQuickQuantity(quantityInput, normalizeQuickQuantity(quantityInput.value) + 1);
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setQuickQuantity(quantityInput, Math.max(0, normalizeQuickQuantity(quantityInput.value) - 1));
      }
    });

    document.addEventListener('click', function(e) {
      const quantityInput = e.target.closest('[data-quick-quantity]');
      if (!quantityInput) return;

      e.stopPropagation();
    });
  }

  function initQuickRemoveButtons() {
    document.addEventListener('click', async function(e) {
      const quickRemoveBtn = e.target.closest('[data-quick-remove]');
      if (!quickRemoveBtn) return;

      e.preventDefault();
      e.stopPropagation();

      const variantId = parseInt(quickRemoveBtn.dataset.quickRemove, 10);
      if (!variantId) return;

      const originalContent = quickRemoveBtn.innerHTML;
      quickRemoveBtn.disabled = true;
      quickRemoveBtn.innerHTML = '<span class="spinner" style="width:16px;height:16px;border-width:2px;"></span>';

      try {
        const cart = await CartAPI.get();
        const lineIndex = cart.items.findIndex(item => item.variant_id === variantId);

        if (lineIndex !== -1) {
          const item = cart.items[lineIndex];
          const newQuantity = Math.max(0, item.quantity - 1);
          await CartAPI.update(lineIndex + 1, newQuantity);
          document.dispatchEvent(new Event('cart:open'));
          quickRemoveBtn.innerHTML = '✓';
        } else {
          quickRemoveBtn.innerHTML = '−';
        }

        setTimeout(() => {
          quickRemoveBtn.innerHTML = originalContent;
          quickRemoveBtn.disabled = false;
        }, 1200);
      } catch (error) {
        console.error('Quick remove error:', error);
        quickRemoveBtn.innerHTML = '!';
        setTimeout(() => {
          quickRemoveBtn.innerHTML = originalContent;
          quickRemoveBtn.disabled = false;
        }, 1200);
      }
    });
  }

  function getLinePropertiesForCardAdd(addBtn, variantId) {
    const name = i18n.backorderPropertyName;
    const value = i18n.backorderPropertyValue;
    if (!name) return {};

    const card = addBtn.closest('[data-product-card]');
    let isBackorder = addBtn.dataset.isBackorder === 'true';

    if (card) {
      const select = card.querySelector('[data-product-card-variant-select]');
      if (select && variantId) {
        const opt = Array.from(select.options).find(o => String(o.value) === String(variantId));
        if (opt) isBackorder = opt.dataset.isBackorder === 'true';
      }
    }

    if (!isBackorder) return {};
    return { [name]: value };
  }

  function getBackorderCartPropertiesFromElement(el) {
    if (!el || el.dataset.isBackorder !== 'true') return {};
    const name = i18n.backorderPropertyName;
    const value = i18n.backorderPropertyValue;
    if (!name) return {};
    return { [name]: value };
  }

  // ============================================
  // Direct Add to Cart Buttons (e.g. equipment cards)
  // ============================================
  
  function initDirectAddToCartButtons() {
    document.addEventListener('click', async function(e) {
      const addBtn = e.target.closest('[data-add-to-cart]');
      if (!addBtn) return;

      e.preventDefault();
      e.stopPropagation();

      const variantId = addBtn.dataset.addToCart;
      if (!variantId) return;

      const quantityInput = addBtn.hasAttribute('data-quick-add-uses-quantity')
        ? getRelatedQuickQuantityInput(addBtn, variantId)
        : null;
      const requestedQuantity = normalizeQuickQuantity(quantityInput ? quantityInput.value : 0);
      const quantity = requestedQuantity > 0 ? requestedQuantity : 1;

      const originalContent = addBtn.innerHTML;
      addBtn.disabled = true;
      addBtn.innerHTML = '<span class="spinner" style="width:16px;height:16px;border-width:2px;"></span>';

      try {
        const props = getLinePropertiesForCardAdd(addBtn, variantId);
        await CartAPI.add(variantId, quantity, props);
        addBtn.innerHTML = '✓ ' + i18n.addedToCart;

        if (quantityInput) setQuickQuantity(quantityInput, 1);

        document.dispatchEvent(new Event('cart:open'));

        setTimeout(() => {
          addBtn.innerHTML = originalContent;
          addBtn.disabled = false;
        }, 1500);

      } catch (error) {
        console.error('Direct add-to-cart error:', error);
        const errorMsg = error.payload?.description || i18n.addToCartError;
        addBtn.textContent = errorMsg;
        addBtn.classList.add('button--error');
        setTimeout(() => {
          addBtn.innerHTML = originalContent;
          addBtn.classList.remove('button--error');
          addBtn.disabled = false;
        }, 2500);
      }
    });
  }

  function updateProductCardMainImage(productCard, source) {
    const mainImage = productCard.querySelector('[data-product-card-main-image]');
    if (!mainImage || !source || !source.dataset.mediaSrc) return;

    mainImage.src = source.dataset.mediaSrc;

    if (source.dataset.mediaSrcset) {
      mainImage.srcset = source.dataset.mediaSrcset;
    }

    if (source.dataset.mediaAlt) {
      mainImage.alt = source.dataset.mediaAlt;
    }

    if (source.dataset.mediaWidth) {
      mainImage.width = parseInt(source.dataset.mediaWidth, 10);
    }

    if (source.dataset.mediaHeight) {
      mainImage.height = parseInt(source.dataset.mediaHeight, 10);
    }
  }

  function syncProductCardVariantThumbnails(productCard, selectedOption) {
    const thumbnailsWrap = productCard.querySelector('[data-product-card-thumbnails]');
    if (!thumbnailsWrap) return;

    const thumbnails = Array.from(thumbnailsWrap.querySelectorAll('[data-product-card-thumbnail]'));
    const variantId = selectedOption ? selectedOption.value : '';
    const featuredMediaId = selectedOption ? selectedOption.dataset.featuredMediaId : '';

    if (!variantId) {
      thumbnailsWrap.hidden = true;
      thumbnails.forEach(thumbnail => {
        thumbnail.hidden = false;
        thumbnail.classList.remove('product-card__thumbnail--active');
      });
      return;
    }

    let variantThumbnails = thumbnails.filter(thumbnail => {
      const variantIds = (thumbnail.dataset.variantIds || '').split(',').filter(Boolean);
      return variantIds.includes(variantId);
    });

    if (variantThumbnails.length === 0 && featuredMediaId) {
      variantThumbnails = thumbnails.filter(thumbnail => thumbnail.dataset.mediaId === featuredMediaId);
    }

    thumbnailsWrap.hidden = variantThumbnails.length <= 1;
    thumbnails.forEach(thumbnail => {
      thumbnail.hidden = variantThumbnails.length > 1 && !variantThumbnails.includes(thumbnail);
      thumbnail.classList.remove('product-card__thumbnail--active');
    });

    if (variantThumbnails.length > 1) {
      variantThumbnails[0].classList.add('product-card__thumbnail--active');
    }
  }

  function initProductCardVariantSelectors() {
    function flashRequiredVariantSelect(variantSelect) {
      if (!variantSelect) return;

      variantSelect.classList.remove('is-required');
      void variantSelect.offsetWidth;
      variantSelect.classList.add('is-required');
      variantSelect.focus({ preventScroll: true });

      window.setTimeout(() => {
        variantSelect.classList.remove('is-required');
      }, 900);
    }

    function updateProductCardLinks(productCard, variantId) {
      const links = productCard.querySelectorAll('[data-product-card-link]');
      links.forEach(link => {
        const baseHref = link.dataset.baseHref || link.getAttribute('href') || '';
        if (!baseHref) return;

        if (!variantId) {
          link.setAttribute('href', baseHref);
          return;
        }

        const url = new URL(baseHref, window.location.origin);
        url.searchParams.set('variant', variantId);
        link.setAttribute('href', url.pathname + url.search + url.hash);
      });
    }

    document.addEventListener('change', function(e) {
      const variantSelect = e.target.closest('[data-product-card-variant-select]');
      if (!variantSelect) return;

      const productCard = variantSelect.closest('[data-product-card]');
      if (!productCard) return;

      const addButton = productCard.querySelector('[data-product-card-variant-add]');
      if (!addButton) return;

      const label = addButton.querySelector('[data-product-card-variant-button-label]');
      const priceMain = productCard.querySelector('.product-card__price-main');
      const variantQuantityControls = productCard.querySelector('[data-product-card-variant-picker] [data-quick-order-controls]');
      const selectedOption = variantSelect.options[variantSelect.selectedIndex];
      const variantId = variantSelect.value;
      const isAvailable = selectedOption && selectedOption.dataset.available === 'true';
      const selectedVariantId = variantId && isAvailable ? variantId : '';

      addButton.dataset.addToCart = selectedVariantId;
      addButton.disabled = false;
      addButton.setAttribute('aria-disabled', selectedVariantId ? 'false' : 'true');
      updateProductCardLinks(productCard, selectedVariantId);

      if (variantQuantityControls) {
        variantQuantityControls.dataset.variantId = selectedVariantId || '';
        variantQuantityControls.hidden = Boolean(variantId && !isAvailable);
      }

      if (priceMain && selectedOption && variantId) {
        const price = selectedOption.dataset.price || '';
        const compareAtPrice = selectedOption.dataset.compareAtPrice || '';

        if (compareAtPrice) {
          priceMain.innerHTML = `<span class="price price--sale">${price}</span><span class="price price--compare">${compareAtPrice}</span>`;
        } else if (price) {
          priceMain.innerHTML = `<span class="price">${price}</span>`;
        }
      }

      if (selectedOption && selectedOption.dataset.mediaSrc) {
        updateProductCardMainImage(productCard, selectedOption);
      }
      syncProductCardVariantThumbnails(productCard, selectedOption);

      if (window.KoffieCoffeeFormat && typeof window.KoffieCoffeeFormat.updateProductCard === 'function' && variantId) {
        window.KoffieCoffeeFormat.updateProductCard(productCard, String(variantId));
      }

      if (label) {
        if (variantId && !isAvailable) {
          label.textContent = addButton.dataset.soldOutLabel || 'Sold out';
        } else if (variantId && isAvailable && selectedOption && selectedOption.dataset.isBackorder === 'true') {
          label.textContent = addButton.dataset.addLabelDelayed || i18n.addToCartDelayed || addButton.dataset.addLabel || i18n.addToCart;
        } else {
          label.textContent = addButton.dataset.addLabel || i18n.addToCart;
        }
      }

      const delayedCta = Boolean(
        variantId && isAvailable && selectedOption && selectedOption.dataset.isBackorder === 'true'
      );
      addButton.classList.toggle('product-card__add-to-cart--backorder', delayedCta);
    });

    document.addEventListener('click', function(e) {
      const addButton = e.target.closest('[data-product-card-variant-add]');
      if (!addButton || addButton.dataset.addToCart) return;

      const productCard = addButton.closest('[data-product-card]');
      const variantSelect = productCard ? productCard.querySelector('[data-product-card-variant-select]') : null;
      if (variantSelect && !variantSelect.value) {
        flashRequiredVariantSelect(variantSelect);
      }
    });
  }

  function initProductCardThumbnails() {
    document.addEventListener('click', function(e) {
      const thumbnail = e.target.closest('[data-product-card-thumbnail]');
      if (!thumbnail) return;

      e.preventDefault();
      e.stopPropagation();

      const productCard = thumbnail.closest('[data-product-card]');
      if (!productCard) return;

      updateProductCardMainImage(productCard, thumbnail);

      productCard.querySelectorAll('[data-product-card-thumbnail]').forEach(function(item) {
        item.classList.remove('product-card__thumbnail--active');
      });
      thumbnail.classList.add('product-card__thumbnail--active');
    });
  }

  // ============================================
  // Consent Mode Helper
  // ============================================
  
  const ConsentManager = {
    /**
     * Update consent state (call from cookie banner)
     */
    update(consentState) {
      if (typeof gtag === 'function') {
        gtag('consent', 'update', consentState);
      }
      
      // Store consent in localStorage
      localStorage.setItem('cookieConsent', JSON.stringify({
        ...consentState,
        timestamp: Date.now()
      }));
    },

    /**
     * Accept all cookies
     */
    acceptAll() {
      this.update({
        'ad_storage': 'granted',
        'ad_user_data': 'granted',
        'ad_personalization': 'granted',
        'analytics_storage': 'granted',
        'functionality_storage': 'granted',
        'personalization_storage': 'granted'
      });
    },

    /**
     * Accept only necessary cookies
     */
    acceptNecessary() {
      this.update({
        'ad_storage': 'denied',
        'ad_user_data': 'denied',
        'ad_personalization': 'denied',
        'analytics_storage': 'denied',
        'functionality_storage': 'granted',
        'personalization_storage': 'denied'
      });
    },

    /**
     * Check if consent was given
     */
    hasConsent() {
      const consent = localStorage.getItem('cookieConsent');
      return consent !== null;
    }
  };

  // ============================================
  // Lazy Loading
  // ============================================
  
  function initLazyLoading() {
    // Native lazy loading is supported in all modern browsers
    // This is a fallback for additional functionality
    
    if ('loading' in HTMLImageElement.prototype) {
      // Native lazy loading supported
      const lazyImages = document.querySelectorAll('img[loading="lazy"]');
      lazyImages.forEach(img => {
        if (img.dataset.src) {
          img.src = img.dataset.src;
        }
      });
    } else {
      // Fallback for older browsers
      const lazyImages = document.querySelectorAll('img[loading="lazy"]');
      const imageObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            if (img.dataset.src) {
              img.src = img.dataset.src;
            }
            imageObserver.unobserve(img);
          }
        });
      });
      
      lazyImages.forEach(img => imageObserver.observe(img));
    }
  }

  // ============================================
  // Smooth Scroll
  // ============================================
  
  function initSmoothScroll() {
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', function(e) {
        const targetId = this.getAttribute('href');
        if (targetId === '#') return;
        
        const target = document.querySelector(targetId);
        if (target) {
          e.preventDefault();
          
          const headerHeight = document.querySelector('.header')?.offsetHeight || 0;
          const targetPosition = target.getBoundingClientRect().top + window.pageYOffset - headerHeight - 20;
          
          window.scrollTo({
            top: targetPosition,
            behavior: 'smooth'
          });
        }
      });
    });
  }

  // ============================================
  // Format Money Helper
  // ============================================

  function normalizeMoneyValue(value) {
    if (typeof value === 'number') {
      return Number.isInteger(value) ? value : Math.round(value * 100);
    }

    if (typeof value === 'string') {
      const cleanedValue = value.trim().replace(/[^\d,.-]/g, '');
      if (!cleanedValue) return 0;

      // Detect EU format: 1.234,56 (dot as thousands, comma as decimal)
      if (/\.\d{3}(,|$)/.test(cleanedValue)) {
        // EU format: remove thousand separators, replace comma decimal
        const euNormalized = cleanedValue.replace(/\./g, '').replace(',', '.');
        const euValue = Number(euNormalized);
        if (!Number.isNaN(euValue)) return Math.round(euValue * 100);
      }

      const normalizedValue = Number(cleanedValue.replace(',', '.'));
      if (Number.isNaN(normalizedValue)) return 0;

      const hasDecimalSeparator = cleanedValue.includes(',') || cleanedValue.includes('.');
      return hasDecimalSeparator ? Math.round(normalizedValue * 100) : Math.round(normalizedValue);
    }

    return 0;
  }
  
  function formatMoney(value, format = window.shopMoneyFormat || '€{{amount}}') {
    const cents = normalizeMoneyValue(value);

    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, format);
    }

    const absoluteValue = Math.abs(cents);
    const sign = cents < 0 ? '-' : '';
    const amount = (absoluteValue / 100).toFixed(2);
    const amountWithCommaSeparator = amount.replace('.', ',');
    const amountWithoutDecimals = String(Math.round(absoluteValue / 100));

    return sign + format
      .replace(/{{\s*amount_with_comma_separator\s*}}/g, amountWithCommaSeparator)
      .replace(/{{\s*amount_no_decimals\s*}}/g, amountWithoutDecimals)
      .replace(/{{\s*amount\s*}}/g, amountWithCommaSeparator);
  }

  // ============================================
  // Coffee format badges (PDP + product cards)
  // ============================================

  const COFFEE_FMT_SVG = {
    filter: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 4h16l-6 8v6l-4 2v-8L4 4z"/></svg>',
    beans: '<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><ellipse cx="12" cy="12" rx="5" ry="8"/><path d="M12 5c-1 1.5-1 5-1 7s0 5.5 1 7" stroke="#fff" stroke-width="1.2" fill="none"/></svg>',
    pads: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="5" y="7" width="14" height="10" rx="3"/><line x1="9" y1="3" x2="15" y2="3"/></svg>',
    cups: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><ellipse cx="12" cy="14" rx="6" ry="4"/><path d="M6 14V10a6 6 0 0 1 12 0v4"/></svg>',
    decaf: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="9"/><path d="M8 16l8-8M8 8l8 8"/></svg>',
  };

  function coffeeFmtIconImgHtml(kind) {
    const urls = window.coffeeFormatIconUrls || {};
    if (kind === 'decaf') {
      if (urls.decaf) {
        return `<img src="${urls.decaf}" alt="" width="20" height="20" class="coffee-format-decaf__img" loading="lazy" decoding="async">`;
      }
      return COFFEE_FMT_SVG.decaf;
    }
    const u = urls[kind];
    if (u) {
      return `<img src="${u}" alt="" width="24" height="24" class="coffee-format-badge__img" loading="lazy" decoding="async">`;
    }
    return COFFEE_FMT_SVG[kind] || '';
  }

  function escapeAttr(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;');
  }

  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  function buildCoffeeFormatBadgesMarkup(slug, hasDecaf, allowFormat, rootClass) {
    const S = window.coffeeFormatStrings || {};
    const fmtLabel = slug && S[slug] ? S[slug] : '';
    const decafL = S.caffeineFree || '';
    let aria = '';
    if (slug && hasDecaf) aria = [fmtLabel, decafL].filter(Boolean).join(', ');
    else if (slug) aria = fmtLabel;
    else if (hasDecaf) aria = decafL;
    const showSlug = allowFormat && slug;
    if (!showSlug && !hasDecaf) return '';

    let inner = '<div class="coffee-format-badges__stack">';
    if (showSlug) {
      inner += `<div class="coffee-format-badge coffee-format-badge--${slug}" data-coffee-format-slug="${slug}" title="${escapeAttr(fmtLabel)}">`;
      inner += '<span class="coffee-format-badge__icon" aria-hidden="true">';
      inner += coffeeFmtIconImgHtml(slug);
      inner += '</span>';
      inner += `<span class="visually-hidden">${escapeHtml(fmtLabel)}</span></div>`;
    }
    if (hasDecaf) {
      inner += '<div class="coffee-format-decaf" data-coffee-format-decaf="">';
      inner += '<span class="coffee-format-decaf__icon" aria-hidden="true">';
      inner += coffeeFmtIconImgHtml('decaf');
      inner += '</span>';
      inner += `<span class="coffee-format-decaf__label">${escapeHtml(decafL)}</span></div>`;
    }
    inner += '</div>';

    const cls = `coffee-format-badges${rootClass ? ` ${rootClass}` : ''}`;
    const ariaAttr = aria ? ` aria-label="${escapeAttr(aria)}" role="group"` : '';
    return `<div class="${cls}" data-coffee-format-badges"${ariaAttr}>${inner}</div>`;
  }

  window.KoffieCoffeeFormat = {
    updatePdp(variant) {
      const dataEl = document.querySelector('[data-coffee-format-pdp]');
      const wrap = document.querySelector('[data-coffee-format-pdp-wrap]');
      if (!dataEl || !wrap || !variant) return;
      let cfg;
      try {
        cfg = JSON.parse(dataEl.textContent || '{}');
      } catch (e) {
        return;
      }
      const slug = cfg.variants && cfg.variants[String(variant.id)] ? cfg.variants[String(variant.id)] : '';
      const html = buildCoffeeFormatBadgesMarkup(slug, !!cfg.decaf, !!cfg.showFormat, 'coffee-format-badges--product-main');
      wrap.innerHTML = html;
    },

    updateProductCard(card, variantIdStr) {
      if (!card || !variantIdStr) return;
      const cfgEl = card.querySelector('[data-coffee-format-json]');
      const oldRoot = card.querySelector('[data-coffee-format-badges]');
      if (!cfgEl || !oldRoot) return;
      let cfg;
      try {
        cfg = JSON.parse(cfgEl.textContent || '{}');
      } catch (e) {
        return;
      }
      const slug = cfg.variants && cfg.variants[String(variantIdStr)] ? cfg.variants[String(variantIdStr)] : '';
      const html = buildCoffeeFormatBadgesMarkup(slug, !!cfg.decaf, true, 'coffee-format-badges--card');
      const temp = document.createElement('div');
      temp.innerHTML = html.trim();
      const next = temp.firstElementChild;
      if (next) {
        oldRoot.replaceWith(next);
      } else {
        oldRoot.remove();
      }
    },
  };

  window.buildCoffeeFormatBadgesMarkup = buildCoffeeFormatBadgesMarkup;

  // ============================================
  // Initialize
  // ============================================
  
  function init() {
    initAddToCartForms();
    initQuickAddButtons();
    initQuickQuantityControls();
    initQuickRemoveButtons();
    initDirectAddToCartButtons();
    initProductCardVariantSelectors();
    initProductCardThumbnails();
    initLazyLoading();
    initSmoothScroll();
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose to global scope
  window.KoffieCart = CartAPI;
  window.KoffieConsent = ConsentManager;
  window.formatMoney = formatMoney;

})();
